/**
 * <store-availability> — renders per-location stock status for the selected variant.
 *
 * Reads the product's custom.store_availability metafield (embedded as JSON) and shows
 * each retail location with an In / Low / Out chip. Re-renders when the shopper switches
 * variant, listening to the theme's `krown:variant:change` event (dispatched on document
 * with detail.variant). Status codes in the data: "in" | "low" | "out".
 */
if (!customElements.get('store-availability')) {
  class StoreAvailability extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-availability-list]');
      this.data = this._readData();

      this.config = {
        hideOut: this.dataset.hideOut === 'true',
        hidden: (this.dataset.hiddenLocations || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        emptyText: this.dataset.emptyText || 'Not currently available in our stores.',
        labels: { in: this.dataset.labelIn || 'In stock', low: this.dataset.labelLow || 'Low stock', out: this.dataset.labelOut || 'Out of stock' },
        colors: { in: this.dataset.colorIn || '#2E7D32', low: this.dataset.colorLow || '#B26A00', out: this.dataset.colorOut || '#9E9E9E' },
      };

      if (!this.data || !this.list) return;

      this.render(this.dataset.initialVariantId);

      this._onVariantChange = (event) => {
        const variant = event.detail && event.detail.variant;
        if (variant && variant.id != null) this.render(String(variant.id));
      };
      document.addEventListener('krown:variant:change', this._onVariantChange);
    }

    disconnectedCallback() {
      if (this._onVariantChange) document.removeEventListener('krown:variant:change', this._onVariantChange);
    }

    _readData() {
      const tag = this.querySelector('[data-availability]');
      if (!tag) return null;
      try {
        return JSON.parse(tag.textContent);
      } catch (error) {
        console.error('[store-availability] invalid JSON', error);
        return null;
      }
    }

    render(variantId) {
      const variants = this.data.variants || {};
      // Fall back to an optional "default" entry if this variant isn't in the data
      // (handy for testing, and a graceful fallback for variants not yet synced).
      const perLocation = variants[String(variantId)] || this.data.default;

      // Preserve the data's preferred location order, falling back to whatever keys exist.
      const order =
        Array.isArray(this.data.locations_order) && this.data.locations_order.length
          ? this.data.locations_order
          : perLocation
          ? Object.keys(perLocation)
          : [];

      const rows = [];
      if (perLocation) {
        order.forEach((location) => {
          const status = perLocation[location];
          if (!status) return;
          if (this.config.hidden.indexOf(location) !== -1) return;
          if (this.config.hideOut && status === 'out') return;
          rows.push(this._row(location, status));
        });
      }

      this.list.innerHTML = rows.length
        ? rows.join('')
        : `<p class="kt-store-availability__empty">${this._escape(this.config.emptyText)}</p>`;
    }

    _row(location, status) {
      const label = this.config.labels[status] || status;
      const color = this.config.colors[status] || this.config.colors.out;
      return (
        `<div class="kt-store-availability__row">` +
        `<span class="kt-store-availability__location">${this._escape(location)}</span>` +
        `<span class="kt-store-availability__status">` +
        `<span class="kt-store-availability__dot" style="background-color:${color}"></span>` +
        `<span class="kt-store-availability__label" style="color:${color}">${this._escape(label)}</span>` +
        `</span></div>`
      );
    }

    _escape(value) {
      const div = document.createElement('div');
      div.textContent = value == null ? '' : String(value);
      return div.innerHTML;
    }
  }

  customElements.define('store-availability', StoreAvailability);
}